import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  getPcps,
  getLocations,
  getEmployees,
  createPcpConfig,
  updatePcpConfig,
  deactivatePcpConfig,
  reactivatePcpConfig,
} from "../../../../utils/services/control/masterDataService";
import PcpConfigDataGrid from "./PcpConfigDataGrid";
import PcpConfigFormDialog from "./PcpConfigFormDialog";
import ConfirmDialog from "../common/ConfirmDialog";

const PcpConfigTab = () => {
  const [pcps, setPcps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editPcp, setEditPcp] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus !== "") filters.status = filterStatus;
      if (filterLocation) filters.locationId = filterLocation;

      const [pcpData, locData, empData] = await Promise.all([
        getPcps(filters),
        getLocations(),
        getEmployees({ status: 1 }),
      ]);
      setPcps(pcpData);
      setLocations(locData);
      setEmployees(empData);
    } catch (err) {
      console.error("Error loading PCP data:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLocation]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Client-side search
  const filteredPcps = search.trim()
    ? pcps.filter(
        (p) =>
          p.pcpFullName?.toLowerCase().includes(search.toLowerCase()) ||
          p.medicalCenter?.toLowerCase().includes(search.toLowerCase())
      )
    : pcps;

  const handleCreateOpen = () => {
    setEditPcp(null);
    setFormOpen(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEdit = Boolean(editPcp);
      const res = isEdit
        ? await updatePcpConfig(data)
        : await createPcpConfig(data);

      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          isEdit
            ? "PCP configuration updated"
            : "PCP configuration created"
        );
        setFormOpen(false);
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error saving PCP configuration",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    const pcp = confirmAction?.pcp;
    setSaving(true);
    try {
      const res = await deactivatePcpConfig({
        employeePcpId: pcp.employeePcpId,
        locationId: pcp.locationId,
      });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        const msg = res.warning
          ? `Deactivated with warning: ${res.patientsAffected} patients affected`
          : "PCP configuration deactivated";
        showSnack(msg, res.warning ? "warning" : "success");
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error deactivating PCP",
        "error"
      );
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const handleReactivate = async () => {
    const pcp = confirmAction?.pcp;
    setSaving(true);
    try {
      const res = await reactivatePcpConfig({
        employeePcpId: pcp.employeePcpId,
        locationId: pcp.locationId,
      });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("PCP configuration reactivated");
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error reactivating PCP",
        "error"
      );
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search by name or location..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={filterLocation}
            label="Location"
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {locations
              .filter((l) => l.status === 1 && l.clinical)
              .map((l) => (
                <MenuItem key={l.locationId} value={l.locationId}>
                  {l.medicalCenter}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={handleCreateOpen}
          size="small"
        >
          Register PCP
        </Button>
      </Box>

      {/* Data Grid */}
      <PcpConfigDataGrid
        rows={filteredPcps}
        loading={loading}
        onEdit={(pcp) => {
          setEditPcp(pcp);
          setFormOpen(true);
        }}
        onDeactivate={(pcp) =>
          setConfirmAction({ type: "deactivate", pcp })
        }
        onReactivate={(pcp) =>
          setConfirmAction({ type: "reactivate", pcp })
        }
      />

      {/* Form Dialog */}
      <PcpConfigFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        pcpConfig={editPcp}
        employees={employees}
        locations={locations}
        loading={saving}
      />

      {/* Deactivate Confirm */}
      <ConfirmDialog
        open={confirmAction?.type === "deactivate"}
        title="Deactivate PCP Configuration"
        message={`Deactivate PCP config for ${confirmAction?.pcp?.pcpFullName} at ${confirmAction?.pcp?.medicalCenter}? Assigned patients may be affected.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      {/* Reactivate Confirm */}
      <ConfirmDialog
        open={confirmAction?.type === "reactivate"}
        title="Reactivate PCP Configuration"
        message={`Reactivate PCP config for ${confirmAction?.pcp?.pcpFullName} at ${confirmAction?.pcp?.medicalCenter}?`}
        confirmLabel="Reactivate"
        confirmColor="success"
        onConfirm={handleReactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PcpConfigTab;
